import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject }    from 'rxjs/Subject';
import 'rxjs/add/operator/map';
import { Config } from './config';
import { Address } from '../Models/address';
import { User } from '../Models/user';
import { Message } from '../Models/message';
import { Product } from '../Models/product';
import { Order } from '../Models/order';
import { Page } from '../Models/page';
import { ProductFilter } from '../Models/filter';
import { ProductBase } from './product.base';


/**
 * The user service provides profile resources to the user, so the
 * service needs to provide things like wishlist, followers etc. as
 * observables, that may be updated any time in the
 * lifecycle.
 */
@Injectable()
export class UserService extends ProductBase {

  // Keep a marker for profile changes, i.e. when a user starts
  // following a product, etc.
  private wishlistChangedSrc = new Subject<Product>();
  private watchChangedSrc = new Subject<User>();

  wishlistChanged$ = this.wishlistChangedSrc.asObservable();
  watchChanged$ = this.watchChangedSrc.asObservable();

  constructor(private http: HttpClient, private config: Config) {
    super();
  }

  list(): Observable<User[]> {
    return this.http.get<User[]>(this.config.getURL('users'));
  }

  create(user: User): Observable<User> {
    return this.http.post<User>(this.config.getURL('users'), user);
  }

  update(user: User): Observable<User> {

    return this.http.put<User>(user._links.self, user);
  }

  getAddress(user: User): Observable<Address> {
    return this.http.get<Address>(user._links.address);
  }

  updateAddress(user: User, address: Address): Observable<Address> {
    return this.http.patch<Address>(user._links.self, address);
  }

  getWishlist(user: User): Observable<Product[]> {
    return this.http.get<Product[]>(user._links.wishlist);
  }

  getFollowing(user: User): Observable<User[]> {
    return this.http.get<User[]>(user._links.following);
  }

  getProducts(user: User, filter?:ProductFilter): Observable<Page<Product>> {

    var params = this.getParamsFromFilter(filter);

    return this.http.get<Product[]>(user._links.products, { params: params })
      .map(
        data =>
          {
            return this.createPage(data);
          }
      );
  }

  getOrders(user: User): Observable<Order[]> {

    return this.http.get<Order[]>(user._links.orders);
  }

  resetPwd(user: User, data): Observable<any> {

    return this.http.post<Observable<any>>(user._links.reset_password, data);
  }

  unFollow(user: User): Observable<any> {

    return this.http.post<Observable<any>>(user._links.unfollow, {}).map(
      data => {
        this.watchChangedSrc.next(user);
        return data;
      }
    );
  }

  follow(user: User): Observable<any> {

    return this.http.post<Observable<any>>(user._links.follow, {}).map(
      data => {
        this.watchChangedSrc.next(user);
        return data;
      }
    );
  }

  // Notify user service that a new product is being followed
  following(product: Product) {
    this.wishlistChangedSrc.next(product);
  }

  // Notify user service that a product is not being followed anymore
  unfollowing(product: Product) {
    this.wishlistChangedSrc.next(product);
  }
}



// WEBPACK FOOTER //
// ./src/app/Services/user.ts